/**
 * Error Types and Database Error Codes
 * Part of Agent Infrastructure Management Implementation
 * Created: Phase 2 - Foundational Error Handling
 */

// =============================================================================
// Base Error Types
// =============================================================================

export interface ApiError {
  code: string;
  message: string;
  details?: string;
  timestamp: string;
  requestId?: string;
}

export interface ValidationError {
  field: string;
  code: string;
  message: string;
  value?: unknown;
}

export interface DatabaseError extends ApiError {
  sql?: string;
  constraint?: string;
  table?: string;
  column?: string;
}

// =============================================================================
// Agent-Related Error Codes
// =============================================================================

export const AGENT_ERROR_CODES = {
  // Agent Configuration Errors (AGT-1xxx)
  AGENT_NOT_FOUND: 'AGT-1001',
  AGENT_ALREADY_EXISTS: 'AGT-1002',
  INVALID_AGENT_TYPE: 'AGT-1003',
  INVALID_WORKSPACE_ID: 'AGT-1004',
  AGENT_TYPE_NOT_SUPPORTED: 'AGT-1005',
  WORKSPACE_NOT_FOUND: 'AGT-1006',
  
  // Agent Configuration Validation Errors (AGT-2xxx)
  INVALID_EXECUTION_INTERVAL: 'AGT-2001',
  INVALID_CUSTOM_PARAMETERS: 'AGT-2002',
  CUSTOM_PARAMETERS_TOO_LARGE: 'AGT-2003',
  INVALID_HEALTH_SCORE: 'AGT-2004',
  INVALID_FAILURE_COUNT: 'AGT-2005',
  CONFIGURATION_LOCKED: 'AGT-2006',
  INVALID_CONFIGURATION: 'AGT-2007',
  
  // Agent Deployment Errors (AGT-3xxx)
  DEPLOYMENT_NOT_FOUND: 'AGT-3001',
  DEPLOYMENT_ALREADY_EXISTS: 'AGT-3002',
  INVALID_GIT_HASH: 'AGT-3003',
  INVALID_DEPLOYMENT_STATUS: 'AGT-3004',
  DEPLOYMENT_DRIFT_DETECTED: 'AGT-3005',
  DEPLOYMENT_VERIFICATION_FAILED: 'AGT-3006',
  
  // Agent Execution Errors (AGT-4xxx)
  EXECUTION_NOT_FOUND: 'AGT-4001',
  EXECUTION_ALREADY_RUNNING: 'AGT-4002',
  EXECUTION_TIMEOUT: 'AGT-4003',
  EXECUTION_FAILED: 'AGT-4004',
  INVALID_EXECUTION_STATUS: 'AGT-4005',
  EXECUTION_CONTEXT_INVALID: 'AGT-4006',
  
  // Agent Health Monitoring Errors (AGT-5xxx)
  HEALTH_CHECK_FAILED: 'AGT-5001',
  HEALTH_SCORE_CALCULATION_ERROR: 'AGT-5002',
  AUTO_DISABLE_TRIGGERED: 'AGT-5003',
  CONSECUTIVE_FAILURES_EXCEEDED: 'AGT-5004',
  HEALTH_MONITORING_DISABLED: 'AGT-5005',
  
  // Permission and Authorization Errors (AGT-6xxx)
  INSUFFICIENT_PERMISSIONS: 'AGT-6001',
  UNAUTHORIZED_AGENT_ACCESS: 'AGT-6002',
  CAPACITY_ACCESS_DENIED: 'AGT-6003',
  WORKSPACE_ACCESS_DENIED: 'AGT-6004',
  ADMIN_REQUIRED: 'AGT-6005',
  
  // Database Constraint Errors (AGT-7xxx)
  AGENT_WORKSPACE_TYPE_CONFLICT: 'AGT-7001',
  DEPLOYMENT_WORKSPACE_TYPE_CONFLICT: 'AGT-7002',
  FOREIGN_KEY_CONSTRAINT_VIOLATION: 'AGT-7003',
  CHECK_CONSTRAINT_VIOLATION: 'AGT-7004',
  UNIQUE_CONSTRAINT_VIOLATION: 'AGT-7005',
  
  // API and Network Errors (AGT-8xxx)
  API_RATE_LIMIT_EXCEEDED: 'AGT-8001',
  API_TIMEOUT: 'AGT-8002',
  NETWORK_CONNECTIVITY_ERROR: 'AGT-8003',
  SERVICE_UNAVAILABLE: 'AGT-8004',
  INVALID_API_REQUEST: 'AGT-8005',
  
  // System and Infrastructure Errors (AGT-9xxx)
  MANAGED_IDENTITY_ERROR: 'AGT-9001',
  KEY_VAULT_ACCESS_ERROR: 'AGT-9002',
  DATABASE_CONNECTION_ERROR: 'AGT-9003',
  TELEMETRY_ERROR: 'AGT-9004',
  CONFIGURATION_ERROR: 'AGT-9005',
} as const;

// =============================================================================
// Error Messages
// =============================================================================

export const AGENT_ERROR_MESSAGES: Record<string, string> = {
  // Agent Configuration Errors (AGT-1xxx)
  [AGENT_ERROR_CODES.AGENT_NOT_FOUND]: 'Agent configuration not found for the specified workspace and type',
  [AGENT_ERROR_CODES.AGENT_ALREADY_EXISTS]: 'Agent configuration already exists for this workspace and type',
  [AGENT_ERROR_CODES.INVALID_AGENT_TYPE]: 'Invalid agent type. Must be lakehouse_maintenance or warehouse_maintenance',
  [AGENT_ERROR_CODES.INVALID_WORKSPACE_ID]: 'Invalid workspace ID format or workspace does not exist',
  [AGENT_ERROR_CODES.AGENT_TYPE_NOT_SUPPORTED]: 'The specified agent type is not supported in this environment',
  [AGENT_ERROR_CODES.WORKSPACE_NOT_FOUND]: 'Workspace not found or access denied',
  
  // Agent Configuration Validation Errors (AGT-2xxx)
  [AGENT_ERROR_CODES.INVALID_EXECUTION_INTERVAL]: 'Execution interval must be between 5 and 1440 minutes',
  [AGENT_ERROR_CODES.INVALID_CUSTOM_PARAMETERS]: 'Custom parameters must be valid JSON format',
  [AGENT_ERROR_CODES.CUSTOM_PARAMETERS_TOO_LARGE]: 'Custom parameters exceed maximum size limit of 4KB',
  [AGENT_ERROR_CODES.INVALID_HEALTH_SCORE]: 'Health score must be between 0 and 100',
  [AGENT_ERROR_CODES.INVALID_FAILURE_COUNT]: 'Consecutive failures count must be between 0 and 10',
  [AGENT_ERROR_CODES.CONFIGURATION_LOCKED]: 'Agent configuration is locked due to ongoing operations',
  [AGENT_ERROR_CODES.INVALID_CONFIGURATION]: 'Agent configuration is invalid or contains conflicting settings',
  
  // Agent Deployment Errors (AGT-3xxx)
  [AGENT_ERROR_CODES.DEPLOYMENT_NOT_FOUND]: 'Deployment record not found for the specified agent',
  [AGENT_ERROR_CODES.DEPLOYMENT_ALREADY_EXISTS]: 'Deployment record already exists for this workspace and agent type',
  [AGENT_ERROR_CODES.INVALID_GIT_HASH]: 'Git commit hash must be a valid 40-character SHA-1 hash',
  [AGENT_ERROR_CODES.INVALID_DEPLOYMENT_STATUS]: 'Invalid deployment status. Must be DEPLOYED, MISSING, MODIFIED, or WORKSPACE_DELETED',
  [AGENT_ERROR_CODES.DEPLOYMENT_DRIFT_DETECTED]: 'Agent deployment drift detected - current state differs from expected',
  [AGENT_ERROR_CODES.DEPLOYMENT_VERIFICATION_FAILED]: 'Failed to verify agent deployment status',
  
  // Agent Execution Errors (AGT-4xxx)
  [AGENT_ERROR_CODES.EXECUTION_NOT_FOUND]: 'Execution record not found',
  [AGENT_ERROR_CODES.EXECUTION_ALREADY_RUNNING]: 'Agent execution is already running for this workspace and type',
  [AGENT_ERROR_CODES.EXECUTION_TIMEOUT]: 'Agent execution timed out before completion',
  [AGENT_ERROR_CODES.EXECUTION_FAILED]: 'Agent execution failed with errors',
  [AGENT_ERROR_CODES.INVALID_EXECUTION_STATUS]: 'Invalid execution status. Must be Running, Completed, Failed, or Timeout',
  [AGENT_ERROR_CODES.EXECUTION_CONTEXT_INVALID]: 'Execution context must be valid JSON format',
  
  // Agent Health Monitoring Errors (AGT-5xxx)
  [AGENT_ERROR_CODES.HEALTH_CHECK_FAILED]: 'Agent health check failed to complete',
  [AGENT_ERROR_CODES.HEALTH_SCORE_CALCULATION_ERROR]: 'Failed to calculate agent health score from execution history',
  [AGENT_ERROR_CODES.AUTO_DISABLE_TRIGGERED]: 'Agent automatically disabled due to consecutive failures',
  [AGENT_ERROR_CODES.CONSECUTIVE_FAILURES_EXCEEDED]: 'Consecutive failure count exceeds maximum threshold',
  [AGENT_ERROR_CODES.HEALTH_MONITORING_DISABLED]: 'Health monitoring is disabled for this agent',
  
  // Permission and Authorization Errors (AGT-6xxx)
  [AGENT_ERROR_CODES.INSUFFICIENT_PERMISSIONS]: 'Insufficient permissions to perform this operation',
  [AGENT_ERROR_CODES.UNAUTHORIZED_AGENT_ACCESS]: 'Unauthorized access to agent configuration',
  [AGENT_ERROR_CODES.CAPACITY_ACCESS_DENIED]: 'Access denied to capacity resources',
  [AGENT_ERROR_CODES.WORKSPACE_ACCESS_DENIED]: 'Access denied to workspace resources',
  [AGENT_ERROR_CODES.ADMIN_REQUIRED]: 'Administrator privileges required for this operation',
  
  // Database Constraint Errors (AGT-7xxx)
  [AGENT_ERROR_CODES.AGENT_WORKSPACE_TYPE_CONFLICT]: 'Agent already exists for this workspace and type combination',
  [AGENT_ERROR_CODES.DEPLOYMENT_WORKSPACE_TYPE_CONFLICT]: 'Deployment already exists for this workspace and agent type',
  [AGENT_ERROR_CODES.FOREIGN_KEY_CONSTRAINT_VIOLATION]: 'Referenced workspace or agent does not exist',
  [AGENT_ERROR_CODES.CHECK_CONSTRAINT_VIOLATION]: 'Data violates database validation rules',
  [AGENT_ERROR_CODES.UNIQUE_CONSTRAINT_VIOLATION]: 'Duplicate key violates uniqueness constraint',
  
  // API and Network Errors (AGT-8xxx)
  [AGENT_ERROR_CODES.API_RATE_LIMIT_EXCEEDED]: 'API rate limit exceeded. Please retry after some time',
  [AGENT_ERROR_CODES.API_TIMEOUT]: 'API request timed out. Please try again',
  [AGENT_ERROR_CODES.NETWORK_CONNECTIVITY_ERROR]: 'Network connectivity error occurred',
  [AGENT_ERROR_CODES.SERVICE_UNAVAILABLE]: 'Agent management service is temporarily unavailable',
  [AGENT_ERROR_CODES.INVALID_API_REQUEST]: 'Invalid API request format or parameters',
  
  // System and Infrastructure Errors (AGT-9xxx)
  [AGENT_ERROR_CODES.MANAGED_IDENTITY_ERROR]: 'Failed to authenticate using managed identity',
  [AGENT_ERROR_CODES.KEY_VAULT_ACCESS_ERROR]: 'Failed to access Azure Key Vault for credentials',
  [AGENT_ERROR_CODES.DATABASE_CONNECTION_ERROR]: 'Database connection error occurred',
  [AGENT_ERROR_CODES.TELEMETRY_ERROR]: 'Failed to log telemetry data',
  [AGENT_ERROR_CODES.CONFIGURATION_ERROR]: 'System configuration error',
};

// =============================================================================
// Error Classification
// =============================================================================

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export enum ErrorCategory {
  VALIDATION = 'validation',
  PERMISSION = 'permission',
  RESOURCE = 'resource',
  SYSTEM = 'system',
  NETWORK = 'network'
}

export interface ErrorClassification {
  code: string;
  severity: ErrorSeverity;
  category: ErrorCategory;
  retryable: boolean;
  userActionRequired: boolean;
}

export const ERROR_CLASSIFICATIONS: Record<string, ErrorClassification> = {
  // Configuration errors - typically user fixable
  [AGENT_ERROR_CODES.INVALID_EXECUTION_INTERVAL]: {
    code: AGENT_ERROR_CODES.INVALID_EXECUTION_INTERVAL,
    severity: ErrorSeverity.MEDIUM,
    category: ErrorCategory.VALIDATION,
    retryable: false,
    userActionRequired: true,
  },
  [AGENT_ERROR_CODES.INVALID_CUSTOM_PARAMETERS]: {
    code: AGENT_ERROR_CODES.INVALID_CUSTOM_PARAMETERS,
    severity: ErrorSeverity.MEDIUM,
    category: ErrorCategory.VALIDATION,
    retryable: false,
    userActionRequired: true,
  },
  
  // Permission errors - usually require admin intervention
  [AGENT_ERROR_CODES.INSUFFICIENT_PERMISSIONS]: {
    code: AGENT_ERROR_CODES.INSUFFICIENT_PERMISSIONS,
    severity: ErrorSeverity.HIGH,
    category: ErrorCategory.PERMISSION,
    retryable: false,
    userActionRequired: true,
  },
  [AGENT_ERROR_CODES.ADMIN_REQUIRED]: {
    code: AGENT_ERROR_CODES.ADMIN_REQUIRED,
    severity: ErrorSeverity.HIGH,
    category: ErrorCategory.PERMISSION,
    retryable: false,
    userActionRequired: true,
  },
  
  // System errors - typically retryable
  [AGENT_ERROR_CODES.DATABASE_CONNECTION_ERROR]: {
    code: AGENT_ERROR_CODES.DATABASE_CONNECTION_ERROR,
    severity: ErrorSeverity.CRITICAL,
    category: ErrorCategory.SYSTEM,
    retryable: true,
    userActionRequired: false,
  },
  [AGENT_ERROR_CODES.SERVICE_UNAVAILABLE]: {
    code: AGENT_ERROR_CODES.SERVICE_UNAVAILABLE,
    severity: ErrorSeverity.HIGH,
    category: ErrorCategory.SYSTEM,
    retryable: true,
    userActionRequired: false,
  },
  
  // Network errors - retryable with backoff
  [AGENT_ERROR_CODES.API_TIMEOUT]: {
    code: AGENT_ERROR_CODES.API_TIMEOUT,
    severity: ErrorSeverity.MEDIUM,
    category: ErrorCategory.NETWORK,
    retryable: true,
    userActionRequired: false,
  },
  [AGENT_ERROR_CODES.NETWORK_CONNECTIVITY_ERROR]: {
    code: AGENT_ERROR_CODES.NETWORK_CONNECTIVITY_ERROR,
    severity: ErrorSeverity.MEDIUM,
    category: ErrorCategory.NETWORK,
    retryable: true,
    userActionRequired: false,
  },
};

// =============================================================================
// Error Handling Utilities
// =============================================================================

export class AgentError extends Error {
  public readonly code: string;
  public readonly severity: ErrorSeverity;
  public readonly category: ErrorCategory;
  public readonly retryable: boolean;
  public readonly userActionRequired: boolean;
  public readonly timestamp: string;
  public readonly details?: string;

  constructor(code: string, details?: string) {
    const message = AGENT_ERROR_MESSAGES[code] || 'Unknown agent error occurred';
    super(message);
    
    this.name = 'AgentError';
    this.code = code;
    this.details = details;
    this.timestamp = new Date().toISOString();
    
    const classification = ERROR_CLASSIFICATIONS[code];
    if (classification) {
      this.severity = classification.severity;
      this.category = classification.category;
      this.retryable = classification.retryable;
      this.userActionRequired = classification.userActionRequired;
    } else {
      this.severity = ErrorSeverity.MEDIUM;
      this.category = ErrorCategory.SYSTEM;
      this.retryable = false;
      this.userActionRequired = true;
    }
  }
}

export const createAgentError = (code: string, details?: string): AgentError => {
  return new AgentError(code, details);
};

export const isRetryableError = (error: Error): boolean => {
  if (error instanceof AgentError) {
    return error.retryable;
  }
  return false;
};

export const getErrorSeverity = (error: Error): ErrorSeverity => {
  if (error instanceof AgentError) {
    return error.severity;
  }
  return ErrorSeverity.MEDIUM;
};

// =============================================================================
// Global Error Handling Types (Feature 034 - Constitution v1.11.0)
// =============================================================================

/**
 * Standard error codes used across the application
 * FR-016: Standardized error response format
 */
export const ErrorCodes = {
  // Client errors (4xx)
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  METHOD_NOT_ALLOWED: 'METHOD_NOT_ALLOWED',
  RATE_LIMITED: 'RATE_LIMITED',           // T005: 429 Too Many Requests
  ACCOUNT_ARCHIVED: 'ACCOUNT_ARCHIVED',   // The joint refusal with FCM: an archived person cannot sign in
  ROLE_NOT_ASSIGNED: 'ROLE_NOT_ASSIGNED', // T005: User has no assigned role
  
  // Server errors (5xx)
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  DATABASE_ERROR: 'DATABASE_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  GATEWAY_TIMEOUT: 'GATEWAY_TIMEOUT',
  BRAIN_UNREACHABLE: 'BRAIN_UNREACHABLE', // Spec 286 FR-015a: the web app could not reach the brain for a worker
  
  // Client-side errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  COMPONENT_ERROR: 'COMPONENT_ERROR',
  
  // Unknown
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export type ErrorCode = typeof ErrorCodes[keyof typeof ErrorCodes];

/**
 * Full error context for telemetry and logging
 * FR-020, FR-021: Central telemetry with full context
 */
export interface ErrorContext {
  /** Error message (sanitized for user display) */
  message: string;
  
  /** Machine-readable error code (e.g., "VALIDATION_ERROR", "NETWORK_ERROR") */
  code: string;
  
  /** Unique correlation ID for tracing (UUID v4) */
  correlationId: string;
  
  /** Short reference ID for user display (e.g., "ERR-A1B2C3D4") */
  referenceId: string;
  
  /** ISO 8601 timestamp */
  timestamp: string;
  
  /** Full stack trace (server-side only, never sent to client) */
  stack?: string;
  
  /** HTTP status code if applicable */
  statusCode?: number;
  
  /** Error severity for alerting */
  severity: ErrorSeverity;
}

/**
 * Frontend-specific error context
 * FR-022: Frontend telemetry includes component name, route, action
 */
export interface FrontendErrorContext extends ErrorContext {
  /** React component name where error occurred */
  component?: string;
  
  /** Current route path */
  route?: string;
  
  /** Action being performed when error occurred */
  action?: string;
  
  /** Authenticated user email (if available) */
  userEmail?: string;
  
  /** Current capacity ID (if operation is capacity-specific) */
  capacityId?: string;
  
  /** Capacity type (Type1/Type2/Type3/Type4) - Constitution XXXI */
  capacityType?: 'Type1' | 'Type2' | 'Type3' | 'Type4';
  
  /** Browser user agent */
  userAgent?: string;
  
  /** Browser info */
  browserInfo?: {
    name: string;
    version: string;
    os: string;
  };
}

/**
 * Backend-specific error context
 * FR-018: Backend logs with correlation ID, user context, stack trace
 */
export interface BackendErrorContext extends ErrorContext {
  /** HTTP method */
  method?: string;
  
  /** Request URL path */
  path?: string;
  
  /** Request body (sanitized - no sensitive data) */
  requestBody?: object;
  
  /** Response time in ms */
  responseTimeMs?: number;
  
  /** User email from JWT */
  userEmail?: string;
  
  /** User role from JWT */
  userRole?: string;
  
  /** Capacity context from request - Constitution XXXI */
  capacityId?: string;
  capacityType?: string;
  
  /** Client ID for Type 2/3 operations */
  clientId?: string;
}

/**
 * Standardized API error response format
 * FR-016: Backend returns errors in standardized format
 */
export interface ApiErrorResponse {
  error: {
    /** Machine-readable error code */
    code: string;
    
    /** User-friendly error message (no technical details) */
    message: string;
    
    /** Additional details (e.g., validation errors) */
    details?: Record<string, unknown>;
    
    /** Correlation ID for support reference */
    correlationId: string;
    
    /** Short reference ID for user display */
    referenceId: string;
  };
}

/**
 * HTTP status code to error code mapping
 */
export const HTTP_STATUS_TO_ERROR_CODE: Record<number, ErrorCode> = {
  400: ErrorCodes.VALIDATION_ERROR,
  401: ErrorCodes.UNAUTHORIZED,
  403: ErrorCodes.FORBIDDEN,
  404: ErrorCodes.NOT_FOUND,
  405: ErrorCodes.METHOD_NOT_ALLOWED,
  409: ErrorCodes.CONFLICT,
  500: ErrorCodes.INTERNAL_ERROR,
  502: ErrorCodes.SERVICE_UNAVAILABLE,
  503: ErrorCodes.SERVICE_UNAVAILABLE,
  504: ErrorCodes.GATEWAY_TIMEOUT,
};

/**
 * User-friendly error messages for each error code
 * FR-019: Sanitized error messages with no implementation details
 */
export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  [ErrorCodes.VALIDATION_ERROR]: 'The request contains invalid data',
  [ErrorCodes.UNAUTHORIZED]: 'Authentication required',
  [ErrorCodes.ACCOUNT_ARCHIVED]: 'This account has been archived and can no longer sign in.',
  [ErrorCodes.FORBIDDEN]: 'You do not have permission to perform this action',
  [ErrorCodes.NOT_FOUND]: 'The requested resource was not found',
  [ErrorCodes.CONFLICT]: 'A resource with this identifier already exists',
  [ErrorCodes.METHOD_NOT_ALLOWED]: 'This operation is not supported',
  [ErrorCodes.INTERNAL_ERROR]: 'An unexpected error occurred. Please try again or contact support.',
  [ErrorCodes.DATABASE_ERROR]: 'A database error occurred. Please try again or contact support.',
  [ErrorCodes.SERVICE_UNAVAILABLE]: 'The service is temporarily unavailable. Please try again later.',
  [ErrorCodes.GATEWAY_TIMEOUT]: 'The request timed out. Please try again.',
  [ErrorCodes.BRAIN_UNREACHABLE]: 'Fabric Control could not be reached. Your worker will try again on its next run.',
  [ErrorCodes.NETWORK_ERROR]: 'Unable to connect. Please check your network connection.',
  [ErrorCodes.TIMEOUT_ERROR]: 'The request timed out. Please try again.',
  [ErrorCodes.COMPONENT_ERROR]: 'Something went wrong. Please refresh the page.',
  [ErrorCodes.UNKNOWN_ERROR]: 'An unexpected error occurred.',
  [ErrorCodes.RATE_LIMITED]: 'Too many requests. Please wait before retrying.',
  [ErrorCodes.ROLE_NOT_ASSIGNED]: 'Your account is pending role assignment. Please contact an administrator.',
};

/**
 * Retryable error codes for automatic retry logic
 * FR-028, FR-029: Retry for transient errors only
 */
export const RETRYABLE_STATUS_CODES: number[] = [408, 429, 500, 502, 503, 504];
export const NON_RETRYABLE_STATUS_CODES: number[] = [400, 401, 403, 404, 405, 409];